<app-progress-spinner [show]="isLoading"></app-progress-spinner>

@if (!isLoading) {
  <div class="lab-requisition-info-container">
    <div class="lab-requisition-details">
      @for (field of detailFields; track trackByIndex($index)) {
        <div class="form-row">
          <label [attr.for]="field.id">{{ field.label | translate }}:</label>
          <input [id]="field.id" type="text" [value]="field.value" disabled />
        </div>
      }
      <div class="form-row import-results-row">
        <button type="button" mat-flat-button color="primary" class="import-results-btn"
          (click)="openImportResults()">
          <span class="material-icons">file_upload</span>
          {{ 'Import Results' | translate }}
        </button>
        @if (canDownloadResultTemplate) {
          <button type="button" mat-stroked-button color="primary" class="download-template-btn"
            (click)="downloadResultTemplate()">
            <span class="material-icons">file_download</span>
            {{ 'Download Lab Result Template' | translate }}
          </button>
        }
      </div>
      <div class="form-row attachments-row">
        <label>{{ 'Attachments' | translate }}:</label>
        @if (labRequisitionDetails?.fileAttachments?.length) {
          <div class="attachments-list">
            @for (attachment of labRequisitionDetails.fileAttachments; track trackByIndex($index)) {
              <button type="button" (click)="openAttachment(attachment)" class="attachment-link clickable"
                [title]="'Download ' + attachment.name" [attr.aria-label]="'Download ' + attachment.name">
                <span class="material-icons">attachment</span>
                {{ attachment.name }}
              </button>
            }
          </div>
        } @else {
          <span class="no-attachments">-</span>
        }
      </div>
    </div>
  </div>
}

@if (!isLoading) {
  <div class="custom-theme-table lab-requisition-samples-table">
    <div class="custom-scrollbar table-responsive outer-bordered-table">
      <table mat-table [dataSource]="dataSource" class="mat-elevation-z1" matSort [matSortActive]="sortActive"
        [matSortDirection]="sortDirection" (matSortChange)="onSortChange($event)">
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell text-center" [attr.colspan]="displayedColumns.length">
            {{ 'No samples assigned to this COC' | translate }}
          </td>
        </tr>
        <ng-container matColumnDef="sampleId">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>
            {{ 'Sample ID' | translate }}
            <span class="material-icons sort-indicator" [class.sort-active]="sortIcons.sampleId !== 'unfold_more'">{{ sortIcons.sampleId }}</span>
          </th>
          <td mat-cell *matCellDef="let element" [attr.rowspan]="element.isFirstRow ? element.rowspan : null"
            [style.display]="element.isFirstRow ? '' : 'none'" class="vertical-border">
            {{ element.sample?.sampleNumber || '-' }}
          </td>
        </ng-container>
        <ng-container matColumnDef="sampleCategory">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>
            {{ 'Sample Category' | translate }}
            <span class="material-icons sort-indicator" [class.sort-active]="sortIcons.sampleCategory !== 'unfold_more'">{{ sortIcons.sampleCategory }}</span>
          </th>
          <td mat-cell *matCellDef="let element" [attr.rowspan]="element.isFirstRow ? element.rowspan : null"
            [style.display]="element.isFirstRow ? '' : 'none'" class="vertical-border">
            {{ element.categoryLabel | translate }}
          </td>
        </ng-container>
        <ng-container matColumnDef="agent">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>
            {{ 'Agent' | translate }}
            <span class="material-icons sort-indicator" [class.sort-active]="sortIcons.agent !== 'unfold_more'">{{ sortIcons.agent }}</span>
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.agent?.agentName || '-' }}
          </td>
        </ng-container>
        @for (column of nestedColumns; track trackByIndex($index)) {
          <ng-container [matColumnDef]="column.key">
            <th mat-header-cell *matHeaderCellDef>{{ column.label | translate }}</th>
            <td mat-cell *matCellDef="let element">
              {{ column.getValue(element.agent) }}
            </td>
          </ng-container>
        }
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>
            {{ 'Status' | translate }}
            <span class="material-icons sort-indicator" [class.sort-active]="sortIcons.status !== 'unfold_more'">{{ sortIcons.status }}</span>
          </th>
          <td mat-cell *matCellDef="let element" [attr.rowspan]="element.isFirstRow ? element.rowspan : null"
            [style.display]="element.isFirstRow ? '' : 'none'" class="vertical-border">
            <mat-form-field appearance="fill" class="status-select">
              <mat-select [(value)]="element.sample.sampleStatus" panelClass="status-dropdown-panel"
                (selectionChange)="onStatusChange(element.sample, $event.value)">
                <mat-select-trigger>
                  <span class="status-badge"
                    [ngStyle]="{'background-color': sampleStatusColors[element.sample.sampleStatus]}">
                    {{ element.statusLabel | translate }}
                  </span>
                </mat-select-trigger>
                @for (status of sampleStatusOptions; track trackByIndex($index)) {
                  <mat-option [value]="status.value"
                    class="status-dropdown-options">
                    <span class="status-badge"
                      [ngStyle]="{'background-color': sampleStatusColors[status.value]}">
                      {{ status.label | translate }}
                    </span>
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>{{ 'Actions' | translate }}</th>
          <td mat-cell *matCellDef="let element" [attr.rowspan]="element.isFirstRow ? element.rowspan : null"
            [style.display]="element.isFirstRow ? '' : 'none'" class="vertical-border">
            @if (element.canUnlink) {
              <button mat-icon-button [title]="'Unlink sample from COC' | translate"
                [attr.aria-label]="'Unlink sample from COC' | translate" (click)="onUnlinkSample(element)"
                class="action-button">
                <span class="material-icons">link_off</span>
              </button>
            }
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; let even = even; columns: displayedColumns;" [class.even-row]="even"></tr>
      </table>
    </div>
    <div class="paginator-container">
      <span>{{ 'Displaying' | translate }}</span>
      <mat-paginator [length]="totalSamples" [pageIndex]="pageIndex" [pageSize]="pageSize"
        [pageSizeOptions]="pageSizeOptions" (page)="onPageChange($event)"></mat-paginator>
    </div>
  </div>
}